import ltvClient from './ltv-client';
import type { LtvResponse, LtvPage, LtvDomainRow, LtvLead, LtvLeadTrafficResponse, LtvLeadDailyResponse, LtvSummary, LtvCategory, LtvInteraction, LtvQuery } from '@/types/ltv';

function qs(params: object): string {
    const p = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') p.append(k, String(v));
    });
    const s = p.toString();
    return s ? `?${s}` : '';
}

// Domínios com dados no período (pra popular o filtro)
export const getLtvDomains = async (params: { start_date: string; end_date: string }): Promise<string[]> => {
    const { data } = await ltvClient.get<{ domains: string[] }>(`/ltv/domains${qs(params)}`);
    return data.domains ?? [];
};

// Países com dados no período (pra popular o filtro)
export const getLtvCountries = async (params: { start_date: string; end_date: string }): Promise<string[]> => {
    const { data } = await ltvClient.get<{ countries: string[] }>(`/ltv/countries${qs(params)}`);
    return data.countries ?? [];
};

// Categorias internas (page_categories)
export const getLtvCategories = async (): Promise<LtvCategory[]> => {
    const { data } = await ltvClient.get<{ categories: LtvCategory[] }>(`/ltv/categories`);
    return data.categories ?? [];
};

// Cards de KPI (leads, clicks, pageviews, páginas) com delta e sparkline
export const getLtvSummary = async (params: { start_date: string; end_date: string; domain?: string; country?: string; category_id?: string }): Promise<LtvSummary> => {
    const { data } = await ltvClient.get<LtvSummary>(`/ltv/summary${qs(params)}`);
    return data;
};

// Média de interação (dias ativos all-time por lead) — endpoint separado (mais pesado)
export const getLtvInteraction = async (params: { start_date: string; end_date: string; domain?: string; country?: string; category_id?: string }): Promise<LtvInteraction> => {
    const { data } = await ltvClient.get<LtvInteraction>(`/ltv/interaction${qs(params)}`);
    return data;
};

// Nível 1 — todas as páginas (cross-domain)
export const getLtvPages = async (params: LtvQuery): Promise<LtvResponse<LtvPage>> => {
    const { data } = await ltvClient.get<LtvResponse<LtvPage>>(`/ltv/pages${qs(params)}`);
    return data;
};

// Nível 2a — domínios de uma página
export const getLtvPageDomains = async (pId: string, params: LtvQuery): Promise<LtvResponse<LtvDomainRow>> => {
    const { data } = await ltvClient.get<LtvResponse<LtvDomainRow>>(
        `/ltv/pages/${encodeURIComponent(pId)}${qs({ ...params, by_domain: true })}`
    );
    return data;
};

// Nível 2b — leads da página direto (com campo domain)
export const getLtvPageLeadsFlat = async (pId: string, params: LtvQuery): Promise<LtvResponse<LtvLead>> => {
    const { data } = await ltvClient.get<LtvResponse<LtvLead>>(
        `/ltv/pages/${encodeURIComponent(pId)}${qs({ ...params, by_domain: false })}`
    );
    return data;
};

// Nível 3 — leads de um domínio da página
export const getLtvPageLeads = async (
    pId: string,
    domain: string | undefined,
    params: LtvQuery
): Promise<LtvResponse<LtvLead>> => {
    const { data } = await ltvClient.get<LtvResponse<LtvLead>>(
        `/ltv/pages/${encodeURIComponent(pId)}/leads${qs({ ...params, domain })}`
    );
    return data;
};

// Nível 4 — tráfego de um lead (páginas/URLs que ele visitou, cross-domain)
export const getLtvLeadTraffic = async (
    leadId: string,
    params: { start_date: string; end_date: string }
): Promise<LtvLeadTrafficResponse> => {
    const { data } = await ltvClient.get<LtvLeadTrafficResponse>(
        `/ltv/leads/${encodeURIComponent(leadId)}/traffic${qs(params)}`
    );
    return data;
};

// Mapa de dias do lead — receita/acessos/sessões/clicks por dia
export const getLtvLeadDaily = async (
    leadId: string,
    params: { start_date: string; end_date: string; p_id?: string; domain?: string; with_paths?: boolean }
): Promise<LtvLeadDailyResponse> => {
    const { data } = await ltvClient.get<LtvLeadDailyResponse>(
        `/ltv/leads/${encodeURIComponent(leadId)}/daily${qs(params)}`
    );
    return data;
};

// Export CSV (usa o mesmo endpoint com format=csv; baixa como blob com auth)
export const downloadLtvCsv = async (path: string, params: Record<string, unknown>, filename: string): Promise<void> => {
    const res = await ltvClient.get(`${path}${qs({ ...params, format: 'csv' })}`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};
